import React, { useMemo, useRef, useState } from 'react';
import { Input } from '../Input/Input';
import { Select } from '../Select/Select';
import { Switch } from '../Switch/Switch';
import { LookupField } from '../LookupField';
import { Badge } from '../Badge/Badge';
import { AlertDialog } from '../AlertDialog/AlertDialog';
import { Button } from '../Button/Button';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import type { Fetcher } from '../LookupField';
import type { ParameterDefinition } from './ParameterEditor.types';
import { getMinMaxFromValidations, validateParameter } from './ParameterEditor.utils';

interface ParameterEditorDynamicFieldProps {
  parameter: ParameterDefinition;
  value: string;
  originalValue: string;
  onChange: (parameterId: string, value: string) => void;
  onValidationError: (parameterId: string, error: string) => void;
  onEntityDisplayLabel?: (parameterId: string, label: string) => void;
  validationError?: string;
  fetcher: Fetcher;
  readOnly?: boolean;
  onResetToDefault?: (parameterId: string, parameterCode: string) => Promise<void>;
}

export const ParameterEditorDynamicField: React.FC<ParameterEditorDynamicFieldProps> = ({
  parameter,
  value,
  originalValue,
  onChange,
  onValidationError,
  onEntityDisplayLabel,
  validationError,
  fetcher,
  readOnly = false,
  onResetToDefault,
}) => {
  const isModified = value !== originalValue;
  const { min, max, maxLength } = getMinMaxFromValidations(parameter.validations);

  const [isResetting, setIsResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Track the last ID selected by the user in ENTITY fields to prevent
  // LookupField from re-fetching when the parent re-renders with the same ID.
  const lastEntitySelectionRef = useRef<string>('');

  // Wrap fetcher for ENTITY fields to inject whereClause (Section 11.5 doc técnico)
  const entityFetcher: Fetcher = useMemo(() => {
    const whereClause = parameter.entityMapping?.whereClause;
    if (!whereClause) return fetcher;
    return (entity, request) =>
      fetcher(entity, { ...request, rawSqlClause: whereClause } as typeof request);
  }, [fetcher, parameter.entityMapping?.whereClause]);

  const handleBlur = (currentValue: string) => {
    const error = validateParameter(parameter, currentValue);
    onValidationError(parameter.id, error);
  };

  const handleResetToDefault = async () => {
    if (!onResetToDefault) return;
    setIsResetting(true);
    try {
      await onResetToDefault(parameter.id, parameter.code);
      setShowResetConfirm(false);
    } finally {
      setIsResetting(false);
    }
  };

  const renderResetButton = () => {
    if (readOnly || !onResetToDefault || !parameter.isOverridden) return null;
    return (
      <div className="pt-6 shrink-0">
        <Button
          type="outline"
          className="p-2 h-10 w-10 flex items-center justify-center border-border-primary dark:border-dark-border-primary !px-0"
          onClick={() => setShowResetConfirm(true)}
          disabled={isResetting}
          ariaLabel="Restaurar a valor predeterminado"
        >
          <ArrowUturnLeftIcon className={`size-5 text-content-secondary dark:text-dark-content-secondary ${isResetting ? 'animate-spin' : ''}`} aria-hidden="true" />
        </Button>
      </div>
    );
  };

  const renderField = () => {
    switch (parameter.dataType) {
      case 'BOOLEAN':
        return (
          <div className="flex w-full items-start gap-2">
            <div className="flex-1 min-w-0">
              <Switch
                label={parameter.name}
                description={parameter.description}
                checked={value === 'true'}
                disabled={readOnly}
                onCheckedChange={(checked) => {
                  onChange(parameter.id, checked ? 'true' : 'false');
                }}
              />
            </div>
            {renderResetButton()}
          </div>
        );

      case 'LIST':
        return (
          <div className="flex w-full items-start gap-2">
            <div className="flex-1 min-w-0">
              <Select
                label={parameter.name}
                options={
                  (parameter.listOptions ?? [])
                    .slice()
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))
                }
                value={value}
                onChange={(val) => onChange(parameter.id, String(val))}
                disabled={readOnly}
                error={!!validationError}
                selectSize="sm"
              />
            </div>
            {renderResetButton()}
          </div>
        );

      case 'ENTITY': {
        // If value matches what the user just selected, keep null (uncontrolled)
        // so LookupField doesn't re-fetch the record it already has.
        // For pre-existing or externally-set values, pass the ID (controlled)
        // so LookupField fetches and displays the correct record.
        const entityValue =
          value && value === lastEntitySelectionRef.current
            ? null
            : value || null;

        return (
          <div className="flex w-full items-start gap-2">
            <div className="flex-1 min-w-0">
              <LookupField
                entity={parameter.entityMapping?.targetTableName ?? ''}
                label={parameter.name}
                displayFields={[parameter.entityMapping?.descriptionField ?? 'name']}
                value={entityValue}
                onChange={(record, previousRecord) => {
                  const newVal = record ? String(record.Id) : '';
                  lastEntitySelectionRef.current = newVal;
                  onChange(parameter.id, newVal);
                  const descField = parameter.entityMapping?.descriptionField ?? 'name';
                  if (previousRecord) {
                    const prevId = String(previousRecord.Id);
                    const prevLabel = String(previousRecord[descField] ?? '');
                    onEntityDisplayLabel?.(prevId, prevLabel);
                  }
                  if (record) {
                    onEntityDisplayLabel?.(newVal, String(record[descField] ?? ''));
                  }
                }}
                fetcher={entityFetcher}
                readOnly={readOnly}
                error={!!validationError}
                errorText={validationError}
              />
            </div>
            {renderResetButton()}
          </div>
        );
      }

      case 'INT':
        return (
          <div className="flex w-full items-start gap-2">
            <div className="flex-1 min-w-0">
              <Input
                label={parameter.name}
                type="number"
                step="1"
                min={min}
                max={max}
                value={value}
                onChange={(e) => onChange(parameter.id, e.target.value)}
                onBlur={() => handleBlur(value)}
                disabled={readOnly}
                error={!!validationError}
                description={validationError || parameter.description}
              />
            </div>
            {renderResetButton()}
          </div>
        );

      case 'DECIMAL':
        return (
          <div className="flex w-full items-start gap-2">
            <div className="flex-1 min-w-0">
              <Input
                label={parameter.name}
                type="number"
                step="0.01"
                min={min}
                max={max}
                value={value}
                onChange={(e) => onChange(parameter.id, e.target.value)}
                onBlur={() => handleBlur(value)}
                disabled={readOnly}
                error={!!validationError}
                description={validationError || parameter.description}
              />
            </div>
            {renderResetButton()}
          </div>
        );

      case 'PERCENTAGE':
        return (
          <div className="flex w-full items-start gap-2">
            <div className="flex-1 min-w-0">
              <Input
                label={parameter.name}
                type="number"
                step="0.01"
                min={min ?? 0}
                max={max ?? 100}
                value={value}
                onChange={(e) => onChange(parameter.id, e.target.value)}
                onBlur={() => handleBlur(value)}
                disabled={readOnly}
                error={!!validationError}
                description={validationError || parameter.description}
                endIcon={
                  <span className="text-content-secondary dark:text-dark-content-secondary text-sm">
                    %
                  </span>
                }
              />
            </div>
            {renderResetButton()}
          </div>
        );

      case 'EMAIL':
        return (
          <div className="flex w-full items-start gap-2">
            <div className="flex-1 min-w-0">
              <Input
                label={parameter.name}
                type={parameter.isSensitive ? 'password' : 'email'}
                value={value}
                onChange={(e) => onChange(parameter.id, e.target.value)}
                onBlur={() => handleBlur(value)}
                disabled={readOnly}
                error={!!validationError}
                description={validationError || parameter.description}
                maxLength={maxLength}
              />
            </div>
            {renderResetButton()}
          </div>
        );

      case 'URL':
        return (
          <div className="flex w-full items-start gap-2">
            <div className="flex-1 min-w-0">
              <Input
                label={parameter.name}
                type={parameter.isSensitive ? 'password' : 'url'}
                value={value}
                onChange={(e) => onChange(parameter.id, e.target.value)}
                onBlur={() => handleBlur(value)}
                disabled={readOnly}
                error={!!validationError}
                description={validationError || parameter.description}
                maxLength={maxLength}
              />
            </div>
            {renderResetButton()}
          </div>
        );

      case 'STRING':
        return (
          <div className="flex w-full items-start gap-2">
            <div className="flex-1 min-w-0">
              <Input
                label={parameter.name}
                type={parameter.isSensitive ? 'password' : 'text'}
                value={value}
                onChange={(e) => onChange(parameter.id, e.target.value)}
                onBlur={() => handleBlur(value)}
                disabled={readOnly}
                error={!!validationError}
                description={validationError || parameter.description}
                maxLength={maxLength}
              />
            </div>
            {renderResetButton()}
          </div>
        );

      default:
        return (
          <div className="flex w-full items-start gap-2">
            <div className="flex-1 min-w-0">
              <Input
                label={parameter.name}
                type="text"
                value="Tipo no soportado"
                disabled
                description={`Tipo de dato "${parameter.dataType}" no es soportado.`}
              />
            </div>
            {renderResetButton()}
          </div>
        );
    }
  };

  return (
    <>
      <div className="relative flex flex-col min-w-0">
        {isModified && (
          <div className="absolute -top-1 right-0 z-10">
            <Badge color="blue" label="Modificado" />
          </div>
        )}
        {renderField()}
      </div>

      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={(e) => {
          if (e.target === e.currentTarget && !isResetting) {
            setShowResetConfirm(false);
          }
        }}>
          <AlertDialog
            title="¿Restaurar valor predeterminado?"
            isProcess={isResetting}
            onCancel={() => setShowResetConfirm(false)}
            onConfirm={handleResetToDefault}
          >
            <p className="text-sm leading-5 font-normal text-content-tertiary dark:text-content-tertiary">
              {`El parámetro "${parameter.name}" volverá a su valor configurado a nivel global. Cualquier personalización se perderá.`}
            </p>
          </AlertDialog>
        </div>
      )}
    </>
  );
};
